import bytes from 'bytes';
import { Location } from 'history';

import { ADMIN, BACK_OFFICE } from '../constants/accountType';

import { SetUserAccountType } from '../actions/Auth';
import * as AccountsApi from '../api/accounts';
import { store } from '../config/configureStore';

import { MonthToLocal } from './months';
import { isAccountType } from './user';

export function ToNumber(value: number | string | null | boolean = '0') {
  const newValue = parseInt(ToString(value), 10);

  if (isNaN(newValue)) {
    return 0;
  }

  return newValue;
}

export function ToFloat(value: number | string | false | null = '0') {
  const newValue = parseFloat(ToString(value));

  if (!newValue || isNaN(newValue)) {
    return 0;
  }

  return parseFloat(newValue.toFixed(2));
}

export function GetCapitalizeAllWords(lastName = '') {
  lastName = ToString(lastName);

  return lastName
    .split(' ')
    .map(name => Capitalize(name))
    .join(' ');
}

export function GetName(user = { firstName: '', lastName: '' } as Utils.IName) {
  if (!user) {
    return '';
  } else if (!user.firstName) {
    return Capitalize(user.lastName);
  } else if (!user.lastName) {
    return GetCapitalizeAllWords(user.firstName);
  }

  return `${Capitalize(user.lastName)} ${GetCapitalizeAllWords(user.firstName)}`;
}

export function StrWithDefaultValue(value: string | null = '') {
  return ToString(value) || '-';
}

export function Truncate(value = '', maxLength = 40) {
  if (value.length <= maxLength) {
    return value;
  }

  return value.slice(0, maxLength).concat('...');
}

export function Capitalize(value = '') {
  if (!value) {
    return '';
  }

  value = ToString(value);

  return `${value.charAt(0).toUpperCase()}${value.slice(1).toLowerCase()}`;
}

export function ToString(value: string | string[] | number | ArrayBuffer | null | undefined | boolean, defaultVal = '') {
  value = value || defaultVal || '';

  return value.toString();
}

export function IsNaturalGas(location: Location) {
  return /gn/.test(GetUtilityTypeFromLocation(location));
}

export function GetUtilityTypeFromLocation(location: Location) {
  return /natural-gas/.test(ToString(location.pathname)) ? 'gn' : 'ee';
}

export function TimeOut(ms = 100) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

export async function DownloadFile(data: Blob | string = '', fileName = '', options: { type?: string } = {}) {
  try {
    const link = document.createElement('a');

    const blob = typeof data === typeof Blob ? data : new Blob([data], options);

    if (navigator.msSaveBlob) {
      return navigator.msSaveBlob(blob, fileName);
    }

    link.href = URL.createObjectURL(blob);

    link.classList.add('d-none');
    link.setAttribute('download', fileName);

    document.body.appendChild(link);

    link.click();

    await TimeOut(100);

    return document.body.removeChild(link);
  } catch (error) {
    return Promise.reject(error || {});
  }
}

export async function DownloadURI(uri: string = '', fileName = '') {
  try {
    const link = document.createElement('a');

    link.href = uri;

    link.classList.add('d-none');
    link.setAttribute('download', fileName);

    document.body.appendChild(link);

    link.click();

    await TimeOut(100);

    return document.body.removeChild(link);
  } catch (error) {
    return Promise.reject(error || {});
  }
}


export function GetMonthFromSeason(str: string = '') {
  if (!str || str.length <= 4) {
    return '01';
  }

  return str.slice(4);
}

export function GetYearFromSeason(str: string = '') {
  if (!str) {
    return `${new Date().getFullYear()}`;
  }

  return str.slice(0, 4);
}

export function GetSize(file?: File) {
  if (!file || !file.size) {
    return null;
  }
  return bytes(file.size, { decimalPlaces: 1, fixedDecimals: true, unitSeparator: ' ' }).toLowerCase();
}

export function ExportDate(value?: string | number) {
  const date = new Date(value || Date.now());

  const day = date.getDate();
  const month = date.getMonth() + 1;
  const year = date.getFullYear();

  return `${year}${Add0(month)}${Add0(day)} ${Add0(date.getHours())}:${Add0(date.getMinutes())}`;
}

export function StrToDateStr(value?: string | number) {
  const date = new Date(value || Date.now());

  const day = date.getDate();
  const month = date.getMonth() + 1;
  const year = date.getFullYear();

  return `${year}${Add0(month)}${Add0(day)}`;
}

export function Add0(num: number | string = 0, defaultVal = '01') {
  num = ToNumber(num);

  if (isNaN(num) || !num) {
    return defaultVal;
  } else if (num < 10) {
    return `0${num}`;
  }
  return `${num}`;
}

export function TimeStampToDate(timeStamp = Date.now()) {
  return new Date(timeStamp).toISOString().split('T')[0];
}

export function TimeStampToDateAndTime(timeStamp = Date.now()) {
  return new Date(timeStamp).toISOString().split('T').join(' ').split('.')[0];
}

export function IsMonthClose(month: keyof Month.IGetMonths, blockedMonths: boolean[] = [], skipCheckAdmin = false) {
  const state = store.getState();

  const skipBlockMonth =
    !skipCheckAdmin && (isAccountType(ADMIN, state.Auth) || isAccountType(BACK_OFFICE, state.Auth));

  return !skipBlockMonth && blockedMonths && !!blockedMonths.length && !!blockedMonths[ToNumber(MonthToLocal(month)) - 1];
}

export function DateStringToTimeStamp(date = TimeStampToDate(), incress = 2) {
  const year = ToNumber(date.slice(0, 4));
  const month = ToNumber(date.slice(5, 7));
  const day = ToNumber(date.slice(8));

  return new Date(year, month - 1, day + incress).getTime();
}

export function DateStringToMinValue(date = TimeStampToDate()) {
  return TimeStampToDate(DateStringToTimeStamp(date));
}

export function DateStringToMaxValue(date = TimeStampToDate()) {
  return TimeStampToDate(DateStringToTimeStamp(date, 0));
}

export function GetFirstMonthAsDate(months?: [keyof Month.IGetMonths]) {
  if (!months || !months.length) {
    return undefined;
  }
  const foundMonth = months.sort().find(Boolean);

  if (!foundMonth) {
    return undefined;
  }

  return `${foundMonth.slice(0, 4)}-${Add0(foundMonth.slice(4))}-01`;
}

export function GetLastMonthAsDate(months?: [keyof Month.IGetMonths]) {
  if (!months || !months.length) {
    return undefined;
  }

  const foundMonth = months
    .sort()
    .reverse()
    .find(Boolean);

  if (!foundMonth) {
    return undefined;
  }

  const year = ToNumber(foundMonth.slice(0, 4));
  const month = ToNumber(foundMonth.slice(4));

  return `${year}-${Add0(month)}-${Add0(GetLastDayOfMonth({ year, month }))}`;
}

export function GetLastDayOfMonth({ year = 0, month = 0 }) {
  return new Date(year, month, 0).getDate();
}

export async function GetMe() {
  try {
    const state = store.getState();

    if (!state || !state.Auth || !state.Auth.token) {
      return undefined;
    }

    const response = await AccountsApi.GetMe();

    if (isAccountType(response.data.accountTypeName, state.Auth)) {
      return undefined;
    }

    return store.dispatch(SetUserAccountType({
      accountTypeName: response.data.accountTypeName,
      localitySector: response.data.localitySector,
      cityHallId: response.data.cityHallId
    }));
  } catch (error) {
    return Promise.reject(error || {});
  }
}

export function IsArrayNotEmpty(data: any[]) {
  return !!(data && data.length);
}

export function GetMonthsForPayments(date = new Date()) {
  return [1, 2, 3, 4, 5, 6, 7].map(val => `${date.getFullYear()}${Add0(val)}`) as [keyof Month.IGetMonths];
}

export function GetMonthsForPaymentsAdditions(date = new Date()) {
  return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(val => `${date.getFullYear()}${Add0(val)}`) as [keyof Month.IGetMonths];
}

export function DateToMonth(date = new Date()) {
  return `${date.getFullYear()}${Add0(date.getMonth() + 1)}` as keyof Month.IGetMonths;
}
